import * as React from 'react';
import { css, html } from 'react-strict-dom';

import { Api, useApiCallable } from '../../api';
import {
  AccentTextLink,
  DateWithTooltip,
  RouterLink,
  Text,
} from '../../components';
import { useSelectedCompany } from '../../contexts/ClientDataContext';
import { useMyUserClient } from '../../hooks';
import { Model } from '../../models';
import SectionBuilds from './SectionBuilds';
import SectionChangelog from './SectionChangelog';
import SectionReleaseNotes from './SectionReleaseNotes';

type Props = {
  release: Api.Release.V;
  listView: boolean;
  invalidate: () => unknown;
};

export default function ReleaseDetail({
  release,
  listView,
  invalidate,
}: Props) {
  const callApi = useApiCallable();
  const company = useSelectedCompany();
  const myUser = useMyUserClient();

  return (
    <html.div key={release['@id']}>
      <VersionName
        to={
          listView
            ? `/company/${company.slug}/releases/${release.versionNumber}`
            : null
        }
      >
        {Model.Release.getVersionName(release)}
      </VersionName>

      <html.ul style={styles.stats}>
        <Text as="li" style={styles.statItem}>
          QA started:{' '}
          <Text as="strong" bold style={styles.statValue}>
            <DateWithTooltip timestamp={release.createdAt} />
          </Text>
        </Text>
        {release.clientTestingStartedAt && (
          <Text as="li" style={styles.statItem}>
            Client testing started:{' '}
            <Text as="strong" bold style={styles.statValue}>
              <DateWithTooltip timestamp={release.clientTestingStartedAt} />
            </Text>
            {!myUser.agencyRole && !release.clientApprovedAt && (
              <>
                {' '}
                - Finished testing?{' '}
                <AccentTextLink
                  onClick={async () => {
                    await callApi(`releases/${release.id}/client-approve`, {
                      method: 'POST',
                    });
                    invalidate();
                  }}
                >
                  Approve this release to go live
                </AccentTextLink>
              </>
            )}
          </Text>
        )}
        {release.clientApprovedAt && (
          <Text as="li" style={styles.statItem}>
            Client approved for release:{' '}
            <Text as="strong" bold style={styles.statValue}>
              <DateWithTooltip timestamp={release.clientApprovedAt} />
            </Text>{' '}
            <i
              className="fa-regular fa-circle-check"
              style={{ color: 'rgb(26, 134, 58)' }}
            />
            {release.clientApprovedBy && (
              <Text>
                {' '}
                by {release.clientApprovedBy.firstName}{' '}
                {release.clientApprovedBy.lastName}
              </Text>
            )}
          </Text>
        )}
      </html.ul>

      <SectionBuilds release={release} invalidate={invalidate} />

      <SectionChangelog release={release} invalidate={invalidate} />

      <SectionReleaseNotes release={release} invalidate={invalidate} />

      {release.tickets.length > 0 && (
        <>
          <Text as="h3" style={styles.sectionHeading}>
            Resolved tickets
          </Text>
          <html.ul style={styles.ticketsList}>
            {[...release.tickets]
              .sort((a, b) => b.number - a.number)
              .map((ticket) => (
                <html.li key={ticket['@id']}>
                  <RouterLink
                    to={`/company/${company.slug}/tickets/${ticket.number}`}
                  >
                    <Text style={styles.ticketLink}>
                      #{ticket.number} {ticket.title}
                    </Text>
                  </RouterLink>
                </html.li>
              ))}
          </html.ul>
        </>
      )}
    </html.div>
  );
}

type VersionNameProps = {
  to: string | null;
  children: React.ReactNode;
};

function VersionName({ to, children }: VersionNameProps) {
  if (to) {
    return (
      <RouterLink to={to}>
        <Text as="h2" style={styles.versionName}>
          {children}
        </Text>
      </RouterLink>
    );
  }

  return (
    <Text as="h2" style={styles.versionName}>
      {children}
    </Text>
  );
}

const styles = css.create({
  versionName: {
    color: '#333',
    fontSize: 21,
    fontWeight: '600',
    margin: 0,
  },
  stats: {
    padding: 0,
    listStyleType: 'none',
    color: '#777',
    marginBlock: '1em',
  },
  statItem: {
    marginBlockEnd: 5,
  },
  statValue: {
    color: '#333',
    fontWeight: '600',
  },
  ticketsList: {
    listStyleType: 'disc',
    paddingInlineStart: 40,
    marginBlock: '1em',
  },
  sectionHeading: {
    color: '#1C1D20',
    fontSize: 18,
    fontWeight: '600',
    marginBlockStart: 25,
    marginBlockEnd: 15,
  },
  ticketLink: {
    color: '#0366d6',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
});
